import React, { useState } from 'react';
import { X, Ban, AlertTriangle } from 'lucide-react';
import { useUserManagementStore } from '../../store/userManagementStore';
import type { BlockUserRequest } from '../../services/userManagementService';

interface BlockConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  onSuccess?: () => void;
}

const BlockConfirmationModal: React.FC<BlockConfirmationModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  onSuccess
}) => {
  const { blockUser, isBlockingUser, blockError, clearErrors } = useUserManagementStore();
  
  const [reason, setReason] = useState<BlockUserRequest['reason']>('OTHER');
  const [notes, setNotes] = useState('');
  
  if (!isOpen) return null;

  const reasons = [
    { value: 'HARASSMENT' as const, label: 'Pesterijen/intimidatie' },
    { value: 'SPAM' as const, label: 'Spam berichten' },
    { value: 'INAPPROPRIATE_CONTENT' as const, label: 'Ongepaste inhoud' },
    { value: 'FAKE_PROFILE' as const, label: 'Nepaccount' },
    { value: 'OTHER' as const, label: 'Anders' },
  ];

  const handleClose = () => {
    clearErrors();
    setReason('OTHER');
    setNotes('');
    onClose();
  };

  const handleConfirm = async () => {
    try {
      const blockData: BlockUserRequest = {};
      
      if (reason) {
        blockData.reason = reason;
      }
      
      if (notes.trim()) {
        blockData.notes = notes.trim();
      }
      
      await blockUser(userId, blockData);
      
      // Success callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal
      handleClose();
      
    } catch (error) {
      // Error is handled by the store
      console.error('Failed to block user:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface border border-white/[.08] rounded-card shadow-panel w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[.08]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-pink/[.12] rounded-full flex items-center justify-center">
              <Ban className="w-5 h-5 text-neon-pink-soft" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-fg-bright">
                Gebruiker blokkeren
              </h2>
              <p className="text-sm text-fg-muted">
                {username}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/[.06] rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-fg-faint" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning */}
          <div className="mb-6 p-4 bg-neon-pink/[.08] border border-neon-pink/30 rounded-field">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-neon-pink-soft mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-fg-body">
                <p className="font-semibold mb-1 text-fg-bright">Let op:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Je kunt geen privéberichten meer sturen naar {username}</li>
                  <li>{username} kan geen privéberichten meer naar jou sturen</li>
                  <li>Groepsberichten en -interacties blijven mogelijk</li>
                  <li>Wil je dat {username} uit een groep wordt verwijderd? Neem contact op met de groepseigenaar</li>
                  <li>Je kunt dit later ongedaan maken in je instellingen</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Reason Selection */}
          <div className="mb-4">
            <label htmlFor="reason" className="block text-sm font-semibold text-fg-soft mb-2">
              Reden voor blokkeren (optioneel)
            </label>
            <select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as BlockUserRequest['reason'])}
              className="sf-input"
            >
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Optional Notes */}
          <div className="mb-6">
            <label htmlFor="notes" className="block text-sm font-semibold text-fg-soft mb-2">
              Notities (optioneel)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Eventuele extra informatie..."
              rows={3}
              className="sf-input resize-none"
              maxLength={500}
            />
            <div className="text-right font-mono text-[11px] text-fg-meta mt-1">
              {notes.length}/500
            </div>
          </div>

          {/* Error message */}
          {blockError && (
            <div className="mb-4 p-3 bg-neon-pink/[.08] border border-neon-pink/30 rounded-field">
              <p className="text-sm text-neon-pink-soft">{blockError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="sf-btn-secondary flex-1 px-4 py-2"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isBlockingUser}
              className="sf-btn-destructive flex-1 px-4 py-2 gap-2"
            >
              {isBlockingUser ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Blokkeren...
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4" />
                  Blokkeren
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockConfirmationModal;